// WebcamPhotoCapture.jsx
//
// Take-a-photo-with-your-camera for the Capture composer (mission 2026-07-20:
// "for all of the options we should be able to use the computer Camera/Micro-
// phone"). getUserMedia(video) -> live <video> preview -> draw the current frame
// to an offscreen <canvas> -> toBlob('image/jpeg') -> a File the EXISTING capture
// upload path (uploadFile -> media_key -> POST type=photo) accepts unchanged.
//
// Unlike video/audio recording (blocked on a backend CaptureType widening, see
// PR body), a webcam photo is still type=photo, so this ships end-to-end today.
//
// States: idle -> requesting -> ready (live) -> captured (frozen preview) with
// Retake/Use; denied / device-missing / unsupported each get honest recovery
// copy and never a dead end (Cancel returns to the upload option). All camera
// tracks are stopped on unmount, on Use, and on Cancel so the camera light never
// lingers. A11y: labelled controls, aria-live status, keyboard-operable buttons.

import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "../../utils";
import BrandWheelLoader from "../BrandWheelLoader";
import CameraPicker from "./CameraPicker";
import useCameraDevices from "./useCameraDevices";
import { cameraConstraint, cameraErrorFor } from "./cameraDevices";

const Container = styled.div`
  width: 100%;
  background-color: ${colors.background};
  border-radius: 8px;
  border: 1px dashed ${colors.purple};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  gap: 12px;
`;

const Stage = styled.div`
  position: relative;
  width: 100%;
  max-width: 360px;
  aspect-ratio: 4 / 3;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StageVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  /* Mirror the live preview like every selfie camera; the captured frame is
     NOT mirrored (canvas draws the true frame). */
  transform: scaleX(-1);
`;

const StageImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  padding: 8px 18px;
  background-color: ${colors.purple};
  color: ${colors.white};
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  font-family: "DM Sans", sans-serif;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background-color: ${colors.white};
  color: ${colors.purple};
  border: 1px solid ${colors.purple};
`;

const InfoText = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  color: ${colors.darkGrey};
  text-align: center;
`;

const ErrorText = styled(InfoText)`
  color: ${colors.error};
`;

/**
 * @param {object} props
 * @param {(file: File, previewUrl: string) => void} props.onCapture
 * @param {() => void} [props.onCancel] return to the upload option
 * @param {MediaDevices} [props.mediaDevices] injectable for tests
 * @param {boolean} [props.autoStage=false]
 *   ONE-CONFIRM mode (QA-86, Capture composer). The shutter hands the still
 *   straight to `onCapture` instead of parking it behind a second "Use photo"
 *   button — the composer's bottom-right "Capture" is the only confirm, and its
 *   staged file row is where the shot is reviewed (with Replace to retake).
 *   Opt-in so any other consumer keeps the review-then-accept flow.
 */
export default function WebcamPhotoCapture({
  onCapture,
  onCancel,
  mediaDevices,
  // QA-105 — injectable so the camera-memory can be asserted without touching
  // the real localStorage.
  storage,
  autoStage = false,
}) {
  // "busy" (QA-105) is its own status, not a flavour of "error": it is the only
  // one where retrying cannot work until the user quits the app holding the
  // camera, and the only one where switching camera is the real remedy.
  const [status, setStatus] = useState("idle"); // idle|requesting|ready|captured|denied|error|busy
  const [errorMessage, setErrorMessage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const previewUrlRef = useRef(null);
  const capturedFileRef = useRef(null);

  const devices =
    mediaDevices ||
    (typeof navigator !== "undefined" ? navigator.mediaDevices : undefined);

  // QA-105 — enumerate the machine's cameras, remember the chosen one.
  const { cameras, selectedId, selectCamera, refresh, labelFor } = useCameraDevices(
    devices,
    storage
  );

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  const requestStream = useCallback(async (deviceIdOverride) => {
    setErrorMessage(null);
    if (!devices || typeof devices.getUserMedia !== "function") {
      setStatus("error");
      setErrorMessage(
        "This browser can't use the camera in-page. Please upload a photo instead."
      );
      return;
    }
    setStatus("requesting");
    try {
      // QA-105 — honour the chosen camera. `cameraConstraint` pins it with
      // `exact`, because a non-exact deviceId is only a hint the browser may
      // ignore, and a picker that can be silently ignored is worse than none.
      const stream = await devices.getUserMedia({
        video: cameraConstraint(deviceIdOverride ?? selectedId),
        audio: false,
      });
      streamRef.current = stream;
      setStatus("ready");
      // Browsers withhold device LABELS until a camera permission has been
      // granted once, so the first enumeration returns blanks. Now that one is
      // granted, re-enumerate to get the real hardware names into the picker.
      refresh();
    } catch (err) {
      // The error taxonomy — including the busy-camera branch this ticket is
      // about — lives in ./cameraDevices, asserted on its own.
      const { status: nextStatus, message } = cameraErrorFor(err, { subject: "camera" });
      setStatus(nextStatus);
      setErrorMessage(message);
    }
  }, [devices, selectedId, refresh]);

  // Attach the live stream to the <video> once ready.
  useEffect(() => {
    const el = videoRef.current;
    if (status === "ready" && el && streamRef.current) {
      el.srcObject = streamRef.current;
    }
    return () => {
      if (el) el.srcObject = null;
    };
  }, [status]);

  // Tear everything down on unmount so the camera light never lingers.
  useEffect(
    () => () => {
      stopTracks();
      revokePreview();
    },
    [stopTracks, revokePreview]
  );

  const takePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    // QA-85 (Kelly, Mac/webcam): the live preview is CSS-mirrored
    // (StageVideo's `transform: scaleX(-1)`, selfie-style) but this canvas
    // draw used to grab the TRUE, unmirrored frame -- so the saved photo
    // came out flipped left-to-right relative to what she saw while framing
    // the shot. Mirror the canvas draw the same way so the saved output
    // matches the preview she actually looked at.
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setStatus("error");
          setErrorMessage("Could not capture the photo. Please try again.");
          return;
        }
        revokePreview();
        const file = new File([blob], `capture-photo-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        capturedFileRef.current = file;
        // The still exists, so free the camera either way.
        stopTracks();
        if (autoStage) {
          // Hand ownership of the preview URL to the parent; do NOT revoke it
          // here, and do NOT enter the "captured" review state — there is no
          // second confirm in this mode (QA-86).
          previewUrlRef.current = null;
          onCapture && onCapture(file, url);
          return;
        }
        setPreviewUrl(url);
        setStatus("captured");
      },
      "image/jpeg",
      0.92
    );
  }, [autoStage, onCapture, revokePreview, stopTracks]);

  const retake = useCallback(() => {
    revokePreview();
    capturedFileRef.current = null;
    setPreviewUrl(null);
    requestStream();
  }, [revokePreview, requestStream]);

  /**
   * QA-105 — switching camera mid-preview. The current stream MUST be stopped
   * first: some drivers refuse a second concurrent open of a different camera,
   * and leaving the old track running also leaves its indicator light on.
   */
  const handleSelectCamera = useCallback(
    (deviceId) => {
      selectCamera(deviceId);
      if (status === "ready" || status === "busy" || status === "error") {
        stopTracks();
        requestStream(deviceId);
      }
    },
    [selectCamera, status, stopTracks, requestStream]
  );

  const usePhoto = useCallback(() => {
    const file = capturedFileRef.current;
    if (!file) return;
    // Hand ownership of the preview URL to the parent; do NOT revoke it here.
    const url = previewUrlRef.current;
    previewUrlRef.current = null;
    stopTracks();
    onCapture && onCapture(file, url);
  }, [onCapture, stopTracks]);

  const cancel = useCallback(() => {
    stopTracks();
    revokePreview();
    capturedFileRef.current = null;
    setPreviewUrl(null);
    setStatus("idle");
    onCancel && onCancel();
  }, [stopTracks, revokePreview, onCancel]);

  if (status === "idle") {
    return (
      <Container>
        <InfoText>Take a photo with your camera.</InfoText>
        <PrimaryButton type="button" onClick={() => requestStream()}>
          Enable camera
        </PrimaryButton>
      </Container>
    );
  }

  if (status === "requesting") {
    return (
      <Container>
        <BrandWheelLoader variant="medium" label="Waiting for camera permission…" />
        <InfoText>Waiting for camera permission&hellip;</InfoText>
      </Container>
    );
  }

  if (status === "denied" || status === "error" || status === "busy") {
    return (
      <Container data-camera-status={status}>
        <ErrorText role="status" aria-live="polite">
          {errorMessage}
        </ErrorText>
        {/* QA-105 — the picker belongs HERE too, not only on the happy path:
            when one camera is held by another app, choosing a different one is
            the actual remedy, and hiding the control behind a working preview
            makes it unreachable exactly when it is needed. */}
        <CameraPicker
          cameras={cameras}
          selectedId={selectedId}
          onSelect={handleSelectCamera}
          labelFor={labelFor}
        />
        <Row>
          {onCancel && (
            <SecondaryButton type="button" onClick={cancel}>
              Upload instead
            </SecondaryButton>
          )}
          {(status === "denied" || status === "busy") && (
            <PrimaryButton type="button" onClick={() => requestStream()}>
              Try again
            </PrimaryButton>
          )}
        </Row>
      </Container>
    );
  }

  if (status === "captured") {
    return (
      <Container>
        <Stage>
          <StageImg src={previewUrl} alt="Captured photo preview" />
        </Stage>
        <Row>
          <SecondaryButton type="button" onClick={retake}>
            Retake
          </SecondaryButton>
          <PrimaryButton type="button" onClick={usePhoto}>
            Use photo
          </PrimaryButton>
        </Row>
      </Container>
    );
  }

  // ready -> live preview + shutter
  return (
    <Container data-camera-status="ready">
      <CameraPicker
        cameras={cameras}
        selectedId={selectedId}
        onSelect={handleSelectCamera}
        labelFor={labelFor}
      />
      <Stage>
        <StageVideo
          ref={videoRef}
          autoPlay
          muted
          playsInline
          aria-label="Live camera preview"
        />
      </Stage>
      <Row>
        {onCancel && (
          <SecondaryButton type="button" onClick={cancel}>
            Cancel
          </SecondaryButton>
        )}
        <PrimaryButton type="button" onClick={takePhoto}>
          Take photo
        </PrimaryButton>
      </Row>
    </Container>
  );
}
